import { useState } from 'react';
import { Box, Typography, Chip, Button, IconButton, Avatar } from '@mui/joy';
import CreditCardRoundedIcon    from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon      from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon   from '@mui/icons-material/PointOfSaleRounded';
import ReceiptLongRoundedIcon   from '@mui/icons-material/ReceiptLongRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

const MONEY_META = {
    transfer: { label: 'Перевод',  color: '#818cf8', bg: 'rgba(129,140,248,0.10)', icon: <CreditCardRoundedIcon  sx={{ fontSize: 13 }} /> },
    cash:     { label: 'Наличные', color: '#22c55e', bg: 'rgba(34,197,94,0.10)',   icon: <PaymentsRoundedIcon    sx={{ fontSize: 13 }} /> },
    terminal: { label: 'Терминал', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)',  icon: <PointOfSaleRoundedIcon sx={{ fontSize: 13 }} /> },
};

const fmtTime     = (iso) => (iso ? new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '—');
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const fmtMoney    = (v)   => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });

/**
 * Компактная карточка одного логического чека (общий receipt_uid).
 * props:
 *  - group:     Order[] одного чека (обязателен)
 *  - onConfirm: (order)=>void — если передан, у неподтверждённого чека будет «Подтвердить»
 *  - onDelete:  (order)=>void — если передан, будет красная кнопка удаления
 *  - busy:      boolean — блокирует кнопки
 */
export default function CheckCard({ group, onConfirm, onDelete, busy = false }) {
    const [commentOpen, setCommentOpen] = useState(false);
    if (!group || group.length === 0) return null;

    const head           = group[0];
    const groupTotal     = group.reduce((s, o) => s + parseFloat(o.value || 0), 0);
    const allItems       = group.flatMap(o => o.items || []);
    const groupComment   = group.map(o => o.comment).find(c => c && c.trim()) || '';
    const isPending      = group.some(o => o.confirmation_status === 'pending');
    const photographerAt = head.receipt_datetime || head.date_create;
    const adminAt        = head.date_create;
    const hasTwoTimes    = head.receipt_datetime
        && Math.abs(new Date(head.receipt_datetime).getTime() - new Date(head.date_create).getTime()) > 60_000;
    const commentLong    = groupComment.length > 60 || groupComment.includes('\n');

    const confirmDelete = () => {
        if (window.confirm('Удалить чек? Он уйдёт из кассы, выручка пересчитается.')) {
            group.forEach(o => onDelete(o));
        }
    };
    const confirmAll = () => group.forEach(o => o.confirmation_status === 'pending' && onConfirm(o));

    return (
        <Box sx={{
            bgcolor: 'var(--joy-palette-background-level1)',
            border: '1px solid var(--joy-palette-divider)',
            borderLeft: `3px solid ${isPending ? '#f59e0b' : '#22c55e'}`,
            borderRadius: 'var(--joy-radius-lg)',
            p: 1.25,
        }}>
            {/* Строка 1: аватар · имя + статус + оплаты · сумма */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar src={head.photographer_avatar_url} size="sm" sx={{ bgcolor: 'var(--joy-palette-primary-500)' }}>
                    {(head.photographer_name || '').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, minWidth: 0 }} noWrap>
                            {head.photographer_name}
                        </Typography>
                        <Chip size="sm" variant="soft" color={isPending ? 'warning' : 'success'}
                            sx={{ fontSize: 9, fontWeight: 700, '--Chip-minHeight': '18px' }}>
                            {isPending ? 'Не подтверждён' : 'Подтверждён'}
                        </Chip>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.3 }}>
                        {group.map(o => {
                            const meta = MONEY_META[o.money_type] || MONEY_META.transfer;
                            return (
                                <Chip key={o.id} size="sm" startDecorator={meta.icon}
                                    sx={{ bgcolor: meta.bg, color: meta.color, fontSize: 9.5, fontWeight: 700, '--Chip-minHeight': '18px' }}>
                                    {meta.label} · {fmtMoney(o.value)} ₽
                                </Chip>
                            );
                        })}
                    </Box>
                </Box>
                <Typography sx={{ fontSize: 17, fontWeight: 800, whiteSpace: 'nowrap' }}>
                    {fmtMoney(groupTotal)} ₽
                </Typography>
            </Box>

            {/* Позиции */}
            {allItems.length > 0 && (
                <Box sx={{ mt: 0.8, display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                    {allItems.map((it, i) => (
                        <Chip key={i} size="sm" variant="soft"
                            sx={{ fontSize: 9.5, fontWeight: 600, bgcolor: 'var(--joy-palette-background-level2)', color: 'var(--joy-palette-text-secondary)', '--Chip-minHeight': '18px' }}>
                            {it.product_name_snapshot}{it.quantity > 1 ? ` × ${it.quantity}` : ''}
                        </Chip>
                    ))}
                </Box>
            )}

            {/* Комментарий — сворачиваемый */}
            {groupComment && (
                <Box
                    onClick={commentLong ? () => setCommentOpen(v => !v) : undefined}
                    sx={{
                        mt: 0.8, px: 1, py: 0.6, borderRadius: 'var(--joy-radius-sm)',
                        bgcolor: 'var(--joy-palette-background-level2)',
                        borderLeft: '3px solid #f59e0b',
                        cursor: commentLong ? 'pointer' : 'default',
                    }}
                >
                    <Typography sx={{
                        fontSize: 11, color: 'var(--joy-palette-text-secondary)',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        ...(commentLong && !commentOpen ? {
                            display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        } : {}),
                    }}>
                        {groupComment}
                    </Typography>
                    {commentLong && (
                        <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: 'var(--joy-palette-primary-400)', mt: 0.2 }}>
                            {commentOpen ? 'свернуть' : 'развернуть'}
                        </Typography>
                    )}
                </Box>
            )}

            {/* Подвал: кто оформил · дата-время · кнопки */}
            <Box sx={{ mt: 0.8, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', flex: 1, minWidth: 0 }} noWrap>
                    {head.processed_by_name && (
                        <Box component="span" sx={{ fontWeight: 700, color: '#22c55e' }}>{head.processed_by_name}</Box>
                    )}
                    {head.processed_by_name && ' · '}
                    {isPending
                        ? <Box component="span" sx={{ color: '#f59e0b', fontWeight: 700 }}>ожидает подтверждения</Box>
                        : hasTwoTimes
                            ? <>фото {fmtDateTime(photographerAt)} → админ {fmtTime(adminAt)}</>
                            : <>{fmtDateTime(adminAt)}</>}
                </Typography>

                {isPending && onConfirm && (
                    <Button size="sm"
                        startDecorator={<ReceiptLongRoundedIcon sx={{ fontSize: 14 }} />}
                        onClick={confirmAll} disabled={busy}
                        sx={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', color: '#fff', fontWeight: 700, fontSize: 11 }}>
                        Подтвердить
                    </Button>
                )}
                {onDelete && (
                    <IconButton size="sm" variant="solid" color="danger" onClick={confirmDelete} disabled={busy}>
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                )}
            </Box>
        </Box>
    );
}